<template>
  <multiselect
    :label="labelKey"
    :track-by="idKey"
    :disabled="disabled"
    :loading="isLoading"
    :options="items"
    :value="value"
    :placeholder="placeholder"
    select-label=""
    selected-label="Selecionado"
    deselect-label="Desmarcar"
    @input="onInput"
    @open="onOpen"
    @search-change="onSearchChange"
  >
    <template slot="caret" slot-scope="{ toggle }" :toggle="toggle">
      <div
        v-if="value"
        class="multiselect__reset"
        @mousedown.prevent.stop="onReset"
      >
        <i class="fa fa-times-circle" aria-hidden="true" />
      </div>
      <div class="multiselect__select" @mousedown.prevent.stop="toggle()" />
    </template>

    <template slot="noOptions">
      <div style="text-align: center">
        <i class="fa fa-folder-open-o" aria-hidden="true" />
        Nenhuma opção disponível.
      </div>
    </template>

    <template slot="noResult">
      <div style="text-align: center">
        <i class="fa fa-folder-open-o" aria-hidden="true" />
        Nada encontrado
      </div>
    </template>

    <template slot="afterList">
      <div v-if="hasMoreData && hasItems" class="multiselect__hasMore">
        <i class="fa fa-search" aria-hidden="true" />
        Muitas opções, pesquise para filtrar.
      </div>
    </template>
  </multiselect>
</template>

<script>
import Multiselect from 'vue-multiselect'
import qs from 'qs'
import { mapGetters } from 'vuex'
import { debounce } from 'lodash'

export default {
  components: { Multiselect },
  data() {
    return {
      hasMoreData: false,
      isLoading: false,
      hasError: false,
      items: []
    }
  },
  props: {
    placeholder: {
      type: String,
      default: 'Selecione um valor'
    },
    value: {
      type: Object,
      default: undefined
    },
    endpoint: {
      type: String,
      required: true
    },
    idKey: {
      type: String,
      default: 'Id',
      required: false
    },
    labelKey: {
      type: String,
      default: 'Nome',
      required: false
    },
    queryFilters: {
      type: Object,
      default: () => ({}),
      required: false
    },
    requestMethod: {
      type: String,
      default: 'GET'
    },
    filtersInBody: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  methods: {
    onReset() {
      this.$emit('input', null)
      this.$emit('select', null)
    },
    onInput(value) {
      this.$emit('input', value)
      this.$emit('select', value)
    },
    onOpen() {
      this.loadData()
    },
    onSearchChange: debounce(function(searchQuery) {
      this.loadData(searchQuery)
    }, 1000),
    buildRequestHeaders() {
      const headers = new Headers()

      headers.append('Content-Type', 'application/json;charset=UTF-8')
      headers.append('Authorization', `bearer ${this.access_token}`)
      return headers
    },
    buildRequestUrl(searchQuery = '') {
      const baseUrl = process.env.VUE_APP_BASE_URL

      let filters = {
        eng: this.$i18n.locale === 'en'
      }

      if (!this.filtersInBody)
        filters = { ...filters, ...this.buildFilters(searchQuery) }

      const query = qs.stringify(filters)

      return `${baseUrl}/${this.endpoint}?${query}`
    },
    buildRequestBody(searchQuery = '') {
      if (!this.filtersInBody) return undefined

      const filters = this.buildFilters(searchQuery)
      return JSON.stringify(filters)
    },
    buildFilters(searchQuery = '') {
      const filters = {
        ...this.queryFilters,
        pageSize: 30
      }

      if (searchQuery) filters[this.labelKey] = searchQuery

      return filters
    },
    async loadData(searchQuery = '') {
      try {
        this.isLoading = true
        this.hasError = false

        const headers = this.buildRequestHeaders()

        const config = {
          method: this.requestMethod,
          headers,
          mode: 'cors',
          body: this.buildRequestBody(searchQuery)
        }

        const url = this.buildRequestUrl(searchQuery)
        const response = await fetch(url, config)
        const data = await response.json()

        if (data.PageCount > 1) this.hasMoreData = true
        this.items = data.Result
      } catch (e) {
        this.hasError = true
      } finally {
        this.isLoading = false
      }
    }
  },
  computed: {
    ...mapGetters('login', ['access_token', 'expires_in']),
    hasItems() {
      return this.items.length > 0
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style>
.multiselect,
.multiselect__select,
.multiselect__tags {
  height: 34px;
  min-height: 34px;
}

.multiselect__tags {
  border-radius: 3px;
  padding-top: 6px;
}

.multiselect__placeholder {
  padding-top: 0px;
  padding-left: 5px;
}

.multiselect,
.multiselect__input,
.multiselect__single {
  font-size: 14px;
}

.multiselect__element,
.multiselect__option {
  height: 34px;
  min-height: 34px;
}

.multiselect__option {
  padding-top: 9px;
}

.multiselect__option:after {
  line-height: 34px;
}

.multiselect__option--selected.multiselect__option--highlight,
.multiselect__option--selected.multiselect__option--highlight:after {
  background: #d84d51;
}

.multiselect__option--highlight {
  background: #14ab65;
}

.multiselect__reset {
  position: absolute;
  right: 40px;
  top: 7px;
  color: #a6a6a6;
  z-index: 10;
}

.multiselect__reset:hover {
  color: #c7c7c7;
  cursor: pointer;
}

.multiselect--disabled .multiselect__reset {
  display: none;
}

.multiselect__hasMore {
  padding: 10px 0px;
  background: #a6a6a6;
  color: white;
  text-align: center;
  font-weight: 500;
}
</style>
